import * as React from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { difference } from 'lodash'
import includes from 'lodash/includes'
import intersection from 'lodash/intersection'
import xor from 'lodash/xor'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { DEFAULT_PERIOD_TYPE } from 'modules/attendance/components/TimeTracking/Layout/form'
import attendanceCustomPeriods from 'modules/attendance/store/collections/customPeriods'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import employees from 'modules/employees/store/collections/employees'

interface Props {
  onClose: () => void
  currentMonth: moment.Moment
}

const DiscardedEmployees = ({ onClose, currentMonth }: Props) => {
  const currentYear = currentMonth.year()
  const currentMonthString = currentMonth.month()

  const attendableEmployees = employees.active
    .filter((e) => !e.attributes.attendable)
    .map((e) => e.attributes.id)

  const [selection, setSelection] = React.useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: attendableEmployees,
  })

  // Helper to extract IDs from selection state
  const getSelectedIds = (): number[] => {
    if (selection.type === 'manual') {
      return selection.ids
    }
    // For 'all' mode, return all active employee IDs except excluded ones
    return employees.active
      .map((e) => e.attributes.id)
      .filter((id) => !selection.excludedIds.includes(id))
  }

  const toggleExcludeFromTimeTracking = async (changedIds: number[]) => {
    if (changedIds.length <= 0) return

    const changes = employees
      .filter((employee) => includes(changedIds, employee.attributes.id))
      .map((employee) => {
        const attendable = !employee.attributes.attendable
        employee.set({ attendable: attendable })
        return {
          employee_id: employee.attributes.id,
          attendable: attendable,
        }
      })

    const updatedEmployees = await employees.bulkUpdate({
      changes,
    })

    return updatedEmployees
  }

  // HINT: month in moment is 0 based, so 3 is actually april, subtract 1 to compensate
  // this hack is necessary because months on our path isn't 0 based
  const startDate = moment([currentYear, currentMonthString])
  const endDate = moment(startDate).clone().endOf('month')

  const { performAction: onSubmit, running: loading } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const selected = getSelectedIds()

      const trackEvent = () => {
        track(trackingEvents.timeTracking.excludeEmployeesTimeTrackingSubmit, {
          excluded_employees: selected,
        })
      }

      const changedIds = xor(selected, attendableEmployees)
      const startDateStr = dateToString(startDate, 'backend')
      const endDateStr = dateToString(endDate, 'backend')
      const idsToRemove = intersection(changedIds, selected)

      await toggleExcludeFromTimeTracking(changedIds)
      const removeCustomPeriodIds = idsToRemove.map((id) => `${id}/${startDateStr}/${endDateStr}`)
      const includedEmployees = difference(changedIds, idsToRemove)
      attendanceCustomPeriods.remove(removeCustomPeriodIds)

      if (includedEmployees.length > 0) {
        setTimeout(async () => {
          await attendanceCustomPeriods
            .fetch({
              data: {
                start_on: startDateStr,
                end_on: endDateStr,
                employee_ids: includedEmployees,
                period_type: DEFAULT_PERIOD_TYPE,
              },
              change: false,
              add: false,
              remove: false,
            })
            .then((customPeriodData) => {
              attendanceCustomPeriods.add(customPeriodData)
            })
        }, 500)
      }

      trackEvent()
      closePortal()
    }
  )

  return (
    <Modal
      illustration="user-male-female"
      title={i18n.t('time_tracking.modals.discarded_employees.title')}
      description={i18n.t('time_tracking.modals.discarded_employees.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={!loading && <Button submit>{i18n.t('common.submit')}</Button>}
    >
      {loading ? (
        <Box flexDirection="column" justifyContent="center" alignItems="center" height="full">
          <Spinner />
        </Box>
      ) : (
        <EmployeeSelectorV2
          multiple
          value={selection.type === 'manual' ? selection.ids : []}
          onChange={setSelection}
        />
      )}
    </Modal>
  )
}

export default observer(DiscardedEmployees)
